import { Avatar, Button, Checkbox, Input, Table, TableProps } from 'antd';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Button as B79Button } from '@virlewood/board-79';
import * as React from 'react';
import dayjs from 'dayjs';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  IconArchive,
  IconCoin, IconCoins,
  IconDoorExit, IconDots,
  IconHttpDelete,
  IconOutbound, IconPlus, IconRefresh,
  IconSearch,
  IconSelect, IconTransferIn, IconTransferOut, IconTrash,
  IconUsersGroup, IconX
} from '@tabler/icons-react';
import { COLORS } from '@next-is-free-monorepo/globals';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { customersDetailsQueryOptions } from '../../utils/queryOptions';
import { Customer, CustomersGroup, ShopNewsRecipient } from '@next-is-free-monorepo/next-is-free-types';
import { ColumnsType } from 'antd/es/table';
import CModal from '../CModal';
import CustomersAutocomplete from './CustomersAutocomplete';
import { apiV1 } from '../../utils/apiClient';
import MultiGroupSelectModal from './MultiGroupSelectModal';
import {
  Cancel01Icon,
  CheckmarkSquare02Icon,
  Notification01Icon,
  Notification02FreeIcons,
  NotificationOff01FreeIcons,
  NotificationOff02Icon, Select01FreeIcons
} from '@hugeicons/core-free-icons';
import AddPointsModal from './AddPointsModal';
import NAvatar from '../../../../libs/nif-web-components/src/microcomponents/NAvatar';
import { NButton, NModal } from '@next-is-free-monorepo/nif-web-components';

interface CustomersTableProps {
}


const CustomersTable = ({ group }: { group?: CustomersGroup }) => {

  const [selectionEnabled, setSelectionEnabled] = useState<any>(undefined);
  const [bulkRemoveConfirm, setBulkRemoveConfirm] = useState(false);
  const [multiGroupModalOpen, setMultiGroupModalOpen] = useState(false);
  const [addPointsModalOpen, setAddPointsModalOpen] = useState(false);

  const renderRowOptions = (customerId: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <B79Button iconOnly variant={'outline'} size={'sm'} className={'rounded-full'}
                startIcon={'hugeicons:more-horizontal'} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => {
          setSelectedRowIds([customerId]);
          setAddPointsModalOpen(true);
        }}>
          <IconCoins size={16} />
          Assegna Punti
        </DropdownMenuItem>
        <DropdownMenuItem
          variant={group?.type === 'manual' ? 'destructive' : 'default'}
          onSelect={() => {
            group?.type === 'manual' ? removeCustomersFromGroup.mutate([customerId]) : setSelectedRowIds([customerId]);
            setMultiGroupModalOpen(true);
          }}>
          {group?.type === 'manual' ? <IconTransferOut size={16} /> : <IconUsersGroup size={16} />}
          {group?.type === 'manual' ? 'Rimuovi dal Gruppo' : 'Aggiungi a Gruppi'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const columns: ColumnsType<Customer> = [
    {
      title: 'Nome',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (value: string, row) => (
        <Link to={`/customers/$customerId`} params={{ customerId: row.id }} className={'flex items-center gap-2'}>
          {/*  <Avatar size={'small'} className={'bg-primary!'}>
            {row.username?.slice(0, 1)}
          </Avatar>{' '}*/}
          <NAvatar size={'small'} text={row.username} />
          <p>{value || '-'}</p>
        </Link>
      )
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (value: string, row) => <Link to={`/customers/$customerId`}
                                            params={{ customerId: row.id }}>@{value}</Link>
    },
    {
      title: 'Punti Attuali',
      dataIndex: 'points',
      key: 'points',
      sorter: (a, b) => a.points - b.points,
      render: (value: string) => <p>{value}</p>
    },
    /*  {
        title: 'Spesa totale',
        dataIndex: 'added_at',
        key: 'hour',
        render: (value: string) => <p>30€</p>
      },*/
    {
      title: 'Aggiunto il',
      dataIndex: 'added_at',
      key: 'hour',
      sorter: (a, b) => dayjs(a.added_at).unix() - dayjs(b.added_at).unix(),
      render: (value: string) => (
        <p style={{ textTransform: 'capitalize' }}>
          {dayjs(value).locale('it').format('DD/MM/YYYY HH:mm')}
        </p>
      )
    },
    {
      title: '',
      dataIndex: 'options',
      key: 'options',
      hidden: selectionEnabled,
      render: (value: string, row) => <div className={'flex items-center gap-2'}>
        {!row.notifications_allowed &&
          <HugeiconsIcon className={'text-red-400 bg-red-50 p-1 rounded'} size={28} icon={NotificationOff02Icon} />}
        {renderRowOptions(row.id)}
      </div>
    }
  ];

  const queryClient = useQueryClient();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const rowSelection: TableProps<any>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRowIds(selectedRowKeys as string[]);
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Disabled User', // Column configuration not to be checked
      name: record.name
    })
  };

  const [search, setSearch] = useState('');
  const [customersToAdd, setCustomersToAdd] = useState<ShopNewsRecipient['recipients']>([]);
  const [addCustomersModalOpen, setAddCustomersModalOpen] = useState(false);

  const addCustomersToGroup = {
    mutationFn: async () => {
      await apiV1.put(`/shop/customers/groups/${group?.id}/customers`, {
        customers: customersToAdd.map((item) => item.id),
        action: 'add'
      });
    },
    onSuccess: () => {
      setAddCustomersModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['customers-details', { search, groupId: group?.id }] });
    }
  };

  const removeCustomersFromGroupMutation = {
    mutationFn: async () => {
      await apiV1.put(`/shop/customers/groups/${group?.id}/customers`, { customers: selectedRowIds, action: 'remove' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-details', { search, groupId: group?.id }] });
      setBulkRemoveConfirm(false);
      setSelectionEnabled(false);
    }
  };

  const removeCustomersFromGroup = useMutation({
    mutationFn: async (customerIds: string[]) => {
      await apiV1.put(`/shop/customers/groups/${group?.id}/customers`, { customers: customerIds, action: 'remove' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-details', { search, groupId: group?.id }] });
    }
  });


  const customers = useQuery({ ...customersDetailsQueryOptions({ search, groupId: group?.id }), placeholderData: [] });

  return (
    <div className={'border rounded-[10px] overflow-hidden'}>
      {
        !!selectionEnabled ?
          <div className={'flex items-center justify-between gap-4 p-2 bg-primary/30 animate mb-5'}>
            <div className={'flex gap-2'}>
              {group ?
                <Button onClick={() => setBulkRemoveConfirm(true)} disabled={!selectedRowIds.length}
                        icon={<IconTransferOut size={14} />} style={{ backgroundColor: 'white' }} danger>Rimuovi dal
                  gruppo</Button>
                :
                <Button onClick={() => setMultiGroupModalOpen(true)} disabled={!selectedRowIds.length}
                        icon={<IconUsersGroup size={14} />}
                        style={{ backgroundColor: 'white' }}>Aggiungi a un
                  Gruppo</Button>
              }
            </div>
            <NButton size={'small'} hugeIcon={Cancel01Icon}
                    onClick={() => setSelectionEnabled(undefined)}>
              Annulla
            </NButton>
          </div>
          :
          <div className={'flex items-center justify-between gap-4 p-2 mb-5'}>
            <Input onChange={(e) => setSearch(e.target.value)} className={'max-w-[300px]'}
                   prefix={<IconSearch size={14} className={'text-slate-300'} />}
                   placeholder={'Cerca nome o username...'} />
            <div className={'flex gap-2'}>
              <NButton hugeIcon={CheckmarkSquare02Icon} color={'tertiary'} size={'small'}
                      onClick={() => setSelectionEnabled(rowSelection)}>Seleziona</NButton>
              {group?.type === 'manual' &&
                <Button onClick={() => setAddCustomersModalOpen(true)} icon={<IconPlus size={14} />} type={'primary'}>Aggiungi
                  Clienti</Button>
              }
            </div>
          </div>
      }
      <Table<Customer>
        rowSelection={selectionEnabled}
        rowHoverable={false}
        rowKey={'id'}
        /*    onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  navigate({ to: `/customers/${record.username}` });
                }
              };
            }}*/
        loading={customers.isLoading}
        dataSource={customers.data || []}
        size={'small'}
        columns={columns}
        pagination={{ size: 'small', pageSize: 50 }}
      >

      </Table>
      <MultiGroupSelectModal open={multiGroupModalOpen} onClose={() => {
        setSelectionEnabled(false);
        setMultiGroupModalOpen(false);
      }}
                             customersIds={selectedRowIds} />
      <CModal disabled={!customersToAdd?.length} hideIcon title={'Aggiungi al Gruppo'} open={addCustomersModalOpen}
              icon={<IconPlus />}
              mutationOptions={addCustomersToGroup} onClose={() => setAddCustomersModalOpen(false)}
              confirmButtonText={'Aggiungi'}>
        <div className={'py-5'}>
          <CustomersAutocomplete selectedCustomers={customersToAdd} setSelectedCustomers={setCustomersToAdd} />
        </div>
      </CModal>
      <CModal open={bulkRemoveConfirm} color={'red'}
              title={`Vuoi veramente rimuovere ${selectedRowIds.length} clienti?`}
              text={'I clienti selezionati verranno rimossi da questo gruppo.'}
              onClose={() => setBulkRemoveConfirm(false)}
              icon={<IconTrash />}
              mutationOptions={removeCustomersFromGroupMutation} />
      <AddPointsModal open={addPointsModalOpen} onClose={() => {
        setAddPointsModalOpen(false);
        setSelectedRowIds([]);
      }}
                      customerId={selectedRowIds[0]} />
    </div>
  );
};
export default CustomersTable;

